import { z } from 'zod';
import { SEVERITIES } from '@/lib/constants';

/**
 * Schema for a single release record from the export feed.
 *
 * The upstream feed is loosely typed, so every field is defended with a
 * default. Unknown severities collapse to "Unknown" rather than throwing,
 * which keeps a single malformed record from blanking the whole dashboard.
 */
export const releaseSchema = z
  .object({
    releaseId: z.string().optional(),
    productName: z.string().default('Unknown product'),
    releaseVersion: z.string().default('—'),
    releaseTitle: z.string().default(''),
    releaseDate: z.string().nullable().optional(),
    releaseStatus: z.string().nullable().optional(),
    severity: z
      .string()
      .nullable()
      .optional()
      .transform((v) => (SEVERITIES as readonly string[]).includes(v ?? '') ? v! : 'Unknown'),
    targetAudience: z.string().nullable().optional(),
    breakingChanges: z.boolean().default(false),
    securityUpdate: z.boolean().default(false),
    featureCount: z.coerce.number().default(0),
    bugFixCount: z.coerce.number().default(0),
    deprecatedFeatures: z.array(z.string()).default([]),
    aiSummary: z.string().nullable().optional(),
    keywords: z.array(z.string()).default([]),
    releaseNotesUrl: z.string().nullable().optional(),
    contentHash: z.string().nullable().optional(),
    lastChecked: z.string().nullable().optional(),
  })
  .transform((r) => ({
    ...r,
    // Derive a stable id if the feed omits one.
    releaseId:
      r.releaseId ?? `${r.productName.toLowerCase().replace(/\s+/g, '-')}_${r.releaseVersion}`,
  }));

export type Release = z.infer<typeof releaseSchema>;

/** A single page of the cursor-paginated export endpoint. */
export const releasePageSchema = z.object({
  items: z.array(z.unknown()).default([]),
  nextCursor: z.string().nullable().optional(),
});

/**
 * Parse a raw page, dropping individual records that fail validation instead
 * of failing the whole page. Returns the valid releases plus the next cursor.
 */
export function parsePage(raw: unknown): { items: Release[]; nextCursor: string | null } {
  const page = releasePageSchema.parse(raw);
  const items: Release[] = [];
  for (const candidate of page.items) {
    const result = releaseSchema.safeParse(candidate);
    if (result.success) items.push(result.data);
  }
  return { items, nextCursor: page.nextCursor ?? null };
}
